import React, { useEffect, useRef } from "react";
import { useStudio } from "@/studio/StudioContext";

const fmt = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

export default function DeckWaveform({ deckId, accent }) {
  const { engine, tick } = useStudio();
  const canvasRef = useRef(null);
  const headRef = useRef(null);
  const timeRef = useRef(null);
  const buffer = engine.decks && engine.decks[deckId] ? engine.decks[deckId].buffer : null;

  // Draw the static waveform whenever a new track is loaded.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = (canvas.width = canvas.clientWidth * 2);
    const H = (canvas.height = canvas.clientHeight * 2);
    ctx.clearRect(0, 0, W, H);
    if (!buffer) return;
    const data = buffer.getChannelData(0);
    const bars = Math.floor(W / 3);
    const block = Math.max(1, Math.floor(data.length / bars));
    ctx.fillStyle = accent;
    ctx.globalAlpha = 0.55;
    for (let i = 0; i < bars; i++) {
      let peak = 0;
      for (let j = 0; j < block; j += 64) {
        const v = Math.abs(data[i * block + j] || 0);
        if (v > peak) peak = v;
      }
      const h = Math.max(2, peak * H * 0.9);
      ctx.fillRect(i * 3, (H - h) / 2, 2, h);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer, accent, tick]);

  // Move the playhead + update the timer every frame.
  useEffect(() => {
    let raf;
    const loop = () => {
      const { cur, dur } = engine.deckPosition(deckId);
      if (headRef.current) headRef.current.style.left = dur ? `${(cur / dur) * 100}%` : "0%";
      if (timeRef.current) timeRef.current.textContent = `${fmt(cur)} / ${fmt(dur)}`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [engine, deckId]);

  const onSeek = (e) => {
    const { dur } = engine.deckPosition(deckId);
    if (!dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    engine.seekDeck(deckId, ratio * dur);
  };

  return (
    <div className="w-full" style={{ width: "100%" }}>
      <div
        onClick={onSeek}
        data-testid={`waveform-${deckId}`}
        className="relative w-full h-11 rounded-sm overflow-hidden cursor-pointer bg-[#0a0a0a] border border-[#1f1f1f]"
        title="Click to jump to a spot"
      >
        <canvas ref={canvasRef} className="w-full h-full block" />
        <div
          ref={headRef}
          className="absolute top-0 bottom-0 w-px pointer-events-none"
          style={{ background: accent, boxShadow: `0 0 6px ${accent}`, left: "0%" }}
        />
        {!buffer && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-neutral-700">
            load a track to see the waveform
          </div>
        )}
      </div>
      <div className="flex justify-end mt-0.5">
        <span
          ref={timeRef}
          data-testid={`timer-${deckId}`}
          className="font-mono-data text-[9px]"
          style={{ color: accent }}
        >
          0:00 / 0:00
        </span>
      </div>
    </div>
  );
}
